import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Download, FileText, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import jsPDF from 'jspdf';
import { toast } from '@/hooks/use-toast';
import { usePaymentSchedules } from '@/hooks/usePaymentSchedules';
import { useDeliverables } from '@/hooks/useDeliverables';

interface DealReportGeneratorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deals: any[];
  campaigns: any[];
  metrics: any;
}

type ReportType = 'summary' | 'detailed' | 'financial' | 'timeline';

export const DealReportGenerator: React.FC<DealReportGeneratorProps> = ({
  open,
  onOpenChange,
  deals,
  campaigns,
  metrics,
}) => {
  const [startDate, setStartDate] = useState<Date | undefined>(new Date(new Date().setMonth(new Date().getMonth() - 3)));
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [reportType, setReportType] = useState<ReportType>('summary');
  const [generating, setGenerating] = useState(false);

  const { paymentSchedules } = usePaymentSchedules();
  const { deliverables } = useDeliverables();

  const filterDataByDate = (data: any[], dateField: string) => {
    if (!startDate || !endDate) return data;
    return data.filter(item => {
      const itemDate = new Date(item[dateField]);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const doc = new jsPDF();
      let yPosition = 20;
      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;

      // Filter data by date range
      const filteredDeals = filterDataByDate(deals, 'created_at');
      const filteredPayments = filterDataByDate(paymentSchedules, 'created_at');
      const filteredDeliverables = filterDataByDate(deliverables, 'created_at');

      // Helper function to check if new page is needed
      const checkNewPage = (requiredSpace: number) => {
        if (yPosition + requiredSpace > doc.internal.pageSize.height - 20) {
          doc.addPage();
          yPosition = 20;
        }
      };

      // COVER PAGE
      doc.setFillColor(59, 130, 246); // primary color
      doc.rect(0, 0, pageWidth, 60, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(28);
      doc.text('Deal Performance Report', pageWidth / 2, 35, { align: 'center' });
      
      yPosition = 80;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.text(`Report Type: ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`, margin, yPosition);
      yPosition += 10;
      doc.text(`Period: ${startDate ? format(startDate, 'MMM dd, yyyy') : 'All time'} - ${endDate ? format(endDate, 'MMM dd, yyyy') : 'Present'}`, margin, yPosition);
      yPosition += 10;
      doc.text(`Generated: ${format(new Date(), 'MMM dd, yyyy HH:mm')}`, margin, yPosition);
      
      // EXECUTIVE SUMMARY
      doc.addPage();
      yPosition = 20;
      doc.setFontSize(18);
      doc.setTextColor(59, 130, 246);
      doc.text('Executive Summary', margin, yPosition);
      doc.setLineWidth(0.5);
      doc.setDrawColor(59, 130, 246);
      doc.line(margin, yPosition + 2, pageWidth - margin, yPosition + 2);
      
      yPosition += 15;
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      
      const summaryData = [
        { label: 'Total Deals', value: filteredDeals.length },
        { label: 'Active Deals', value: filteredDeals.filter(d => d.status === 'active').length },
        { label: 'Completed Deals', value: filteredDeals.filter(d => d.status === 'completed').length },
        { label: 'Total Deal Value', value: `$${filteredDeals.reduce((sum, d) => sum + (Number(d.deal_value) || 0), 0).toLocaleString()}` },
        { label: 'Total Payments Due', value: `$${filteredPayments.filter(p => p.status === 'pending' || p.status === 'overdue').reduce((sum, p) => sum + (Number(p.amount) || 0), 0).toLocaleString()}` },
        { label: 'Total Payments Made', value: `$${filteredPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (Number(p.amount) || 0), 0).toLocaleString()}` },
        { label: 'Active Campaigns', value: campaigns.filter(c => c.status === 'active').length },
        { label: 'Deliverables Pending', value: filteredDeliverables.filter(d => d.status === 'pending').length },
      ];

      summaryData.forEach(item => {
        checkNewPage(10);
        doc.setFont(undefined, 'bold');
        doc.text(`${item.label}:`, margin, yPosition);
        doc.setFont(undefined, 'normal');
        doc.text(`${item.value}`, margin + 80, yPosition);
        yPosition += 8;
      });

      // DEAL PERFORMANCE ANALYSIS
      if (reportType === 'summary' || reportType === 'detailed') {
        checkNewPage(40);
        yPosition += 10;
        doc.setFontSize(16);
        doc.setTextColor(59, 130, 246);
        doc.text('Deal Performance Analysis', margin, yPosition);
        doc.line(margin, yPosition + 2, pageWidth - margin, yPosition + 2);
        
        yPosition += 15;
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);

        const dealsByStatus = {
          active: filteredDeals.filter(d => d.status === 'active').length,
          completed: filteredDeals.filter(d => d.status === 'completed').length,
          pending: filteredDeals.filter(d => d.status === 'pending').length,
          cancelled: filteredDeals.filter(d => d.status === 'cancelled').length,
        };

        Object.entries(dealsByStatus).forEach(([status, count]) => {
          checkNewPage(8);
          doc.text(`${status.charAt(0).toUpperCase() + status.slice(1)}: ${count} (${((count / filteredDeals.length) * 100).toFixed(1)}%)`, margin, yPosition);
          yPosition += 7;
        });

        if (reportType === 'detailed') {
          yPosition += 10;
          doc.setFontSize(12);
          doc.setTextColor(59, 130, 246);
          doc.text('Deal Details', margin, yPosition);
          yPosition += 10;

          filteredDeals.slice(0, 15).forEach((deal, index) => {
            checkNewPage(25);
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(`${index + 1}. ${deal.title || 'Untitled Deal'}`, margin, yPosition);
            yPosition += 6;
            
            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text(`Status: ${deal.status} | Value: $${Number(deal.deal_value || 0).toLocaleString()} | Type: ${deal.deal_type || 'N/A'}`, margin + 5, yPosition);
            yPosition += 5;
            doc.text(`Created: ${format(new Date(deal.created_at), 'MMM dd, yyyy')}`, margin + 5, yPosition);
            yPosition += 10;
          });
        }
      }

      // FINANCIAL ANALYSIS
      if (reportType === 'financial' || reportType === 'detailed') {
        checkNewPage(40);
        yPosition += 10;
        doc.setFontSize(16);
        doc.setTextColor(59, 130, 246);
        doc.text('Financial Analysis', margin, yPosition);
        doc.line(margin, yPosition + 2, pageWidth - margin, yPosition + 2);
        
        yPosition += 15;
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);

        const paymentSummary = {
          totalPaid: filteredPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
          totalPending: filteredPayments.filter(p => p.status === 'pending').reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
          totalOverdue: filteredPayments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
        };

        doc.setFont(undefined, 'bold');
        doc.text('Payment Summary:', margin, yPosition);
        yPosition += 8;
        doc.setFont(undefined, 'normal');
        doc.text(`Paid: $${paymentSummary.totalPaid.toLocaleString()}`, margin + 5, yPosition);
        yPosition += 7;
        doc.text(`Pending: $${paymentSummary.totalPending.toLocaleString()}`, margin + 5, yPosition);
        yPosition += 7;
        doc.setTextColor(220, 38, 38);
        doc.text(`Overdue: $${paymentSummary.totalOverdue.toLocaleString()}`, margin + 5, yPosition);
        doc.setTextColor(0, 0, 0);
        yPosition += 12;

        // Upcoming Payments
        const upcomingPayments = filteredPayments
          .filter(p => p.status === 'pending' && new Date(p.due_date) > new Date())
          .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())
          .slice(0, 10);

        if (upcomingPayments.length > 0) {
          checkNewPage(30);
          doc.setFont(undefined, 'bold');
          doc.text('Upcoming Payments:', margin, yPosition);
          yPosition += 8;
          doc.setFont(undefined, 'normal');

          upcomingPayments.forEach(payment => {
            checkNewPage(8);
            doc.setFontSize(9);
            doc.text(`${payment.milestone_name}: $${Number(payment.amount).toLocaleString()} - Due: ${format(new Date(payment.due_date), 'MMM dd, yyyy')}`, margin + 5, yPosition);
            yPosition += 6;
          });
        }
      }

      // TIMELINE & DELIVERABLES
      if (reportType === 'timeline' || reportType === 'detailed') {
        checkNewPage(40);
        yPosition += 10;
        doc.setFontSize(16);
        doc.setTextColor(59, 130, 246);
        doc.text('Timeline & Deliverables', margin, yPosition);
        doc.line(margin, yPosition + 2, pageWidth - margin, yPosition + 2);
        
        yPosition += 15;
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);

        const deliverablesSummary = {
          pending: filteredDeliverables.filter(d => d.status === 'pending').length,
          submitted: filteredDeliverables.filter(d => d.status === 'submitted').length,
          approved: filteredDeliverables.filter(d => d.approval_status === 'approved').length,
          rejected: filteredDeliverables.filter(d => d.approval_status === 'rejected').length,
        };

        doc.setFont(undefined, 'bold');
        doc.text('Deliverables Status:', margin, yPosition);
        yPosition += 8;
        doc.setFont(undefined, 'normal');
        Object.entries(deliverablesSummary).forEach(([status, count]) => {
          checkNewPage(7);
          doc.text(`${status.charAt(0).toUpperCase() + status.slice(1)}: ${count}`, margin + 5, yPosition);
          yPosition += 7;
        });

        // Overdue Deliverables
        const overdueDeliverables = filteredDeliverables
          .filter(d => d.status !== 'completed' && new Date(d.due_date) < new Date())
          .slice(0, 10);

        if (overdueDeliverables.length > 0) {
          yPosition += 10;
          checkNewPage(30);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(220, 38, 38);
          doc.text('Overdue Deliverables:', margin, yPosition);
          doc.setTextColor(0, 0, 0);
          yPosition += 8;
          doc.setFont(undefined, 'normal');

          overdueDeliverables.forEach(deliverable => {
            checkNewPage(8);
            doc.setFontSize(9);
            doc.text(`${deliverable.title} - Due: ${format(new Date(deliverable.due_date), 'MMM dd, yyyy')}`, margin + 5, yPosition);
            yPosition += 6;
          });
        }
      }

      // FOOTER ON LAST PAGE
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });
        doc.text('Confidential - Deal Performance Report', pageWidth / 2, doc.internal.pageSize.height - 5, { align: 'center' });
      }

      // Save the PDF
      const fileName = `Deal_Report_${reportType}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      doc.save(fileName);

      toast({
        title: 'Report Generated',
        description: `Your ${reportType} report has been downloaded successfully.`,
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate report. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Generate Deal Report
          </DialogTitle>
          <DialogDescription>
            Create a comprehensive report of your deals and partnerships with customizable date ranges and report types.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Report Type */}
          <div className="space-y-2">
            <Label>Report Type</Label>
            <Select value={reportType} onValueChange={(value) => setReportType(value as ReportType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="summary">Summary Report</SelectItem>
                <SelectItem value="detailed">Detailed Report</SelectItem>
                <SelectItem value="financial">Financial Report</SelectItem>
                <SelectItem value="timeline">Timeline & Deliverables</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !startDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'MMM dd, yyyy') : 'Pick date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !endDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'MMM dd, yyyy') : 'Pick date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Quick Date Ranges */}
          <div className="space-y-2">
            <Label>Quick Select</Label>
            <div className="grid grid-cols-4 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStartDate(new Date(new Date().setMonth(new Date().getMonth() - 1)));
                  setEndDate(new Date());
                }}
              >
                Last Month
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStartDate(new Date(new Date().setMonth(new Date().getMonth() - 3)));
                  setEndDate(new Date());
                }}
              >
                Last Quarter
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStartDate(new Date(new Date().setMonth(new Date().getMonth() - 6)));
                  setEndDate(new Date());
                }}
              >
                Last 6 Months
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStartDate(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
                  setEndDate(new Date());
                }}
              >
                Last Year
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={generateReport} disabled={generating}>
            {generating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download Report
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
